<template>
  <b-sidebar
    id="add-new-sidebar"
    :visible="isAddNewSidebarActive"
    bg-variant="white"
    sidebar-class="sidebar-lg"
    shadow
    backdrop
    no-header
    right
    @hidden="resetForm"
    @change="(val) => $emit('update:is-add-new-sidebar-active', val)"
  >
    <template #default="{ hide }">
      <!-- Header -->
      <div
        class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
      >
        <h5 class="mb-0">Add New FAQ</h5>

        <feather-icon
          class="ml-1 cursor-pointer"
          icon="XIcon"
          size="16"
          @click="hide"
        />
      </div>

      <!-- BODY -->
      <validation-observer #default="{ handleSubmit }" ref="refFormObserver">
        <!-- Form -->
        <b-form
          class="p-2"
          @submit.prevent="handleSubmit(onSubmit)"
          @reset.prevent="resetForm"
        >
          <!-- Faq Question -->
          <validation-provider
            #default="validationContext"
            name="Question"
            rules="required"
          >
            <b-form-group label="Question" label-for="name">
              <b-form-input
                id="name"
                v-model="catData.name"
                :state="getValidationState(validationContext)"
                trim
              />

              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Faq Answer -->
          <validation-provider
            #default="validationContext"
            name="Answer"
            rules="required"
          >
            <b-form-group label="Answer" label-for="name">
              <b-form-textarea
                id="slug"
                v-model="catData.slug"
                :state="getValidationState(validationContext)"
                trim
                rows="4"
              />

              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Faq belong for -->
          <validation-provider
            #default="validationContext"
            name="belong"
            rules=""
          >
            <b-form-group label="Belong for" label-for="belong">
              <v-select
                v-model="catData.belong"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="faqBelong"
                :reduce="(val) => val.value"
                :clearable="false"
                input-id="type"
              />

              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Faq Language -->
          <validation-provider
            #default="validationContext"
            name="Language"
            rules="required"
          >
            <b-form-group
              label="Language"
              label-for="type"
              :state="getValidationState(validationContext)"
            >
              <v-select
                v-model="catData.language"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="faqLanguage"
                :reduce="(val) => val.value"
                :clearable="false"
                input-id="type"
              />
              <b-form-invalid-feedback
                :state="getValidationState(validationContext)"
              >
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>
          <!-- Faq Type -->
          <validation-provider
            #default="validationContext"
            name="Is Active"
            rules="required"
          >
            <b-form-group
              label="Is Active"
              label-for="type"
              :state="getValidationState(validationContext)"
            >
              <v-select
                v-model="catData.isActive"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="faqStatuses"
                :reduce="(val) => val.value"
                :clearable="false"
                input-id="type"
              />
              <b-form-invalid-feedback
                :state="getValidationState(validationContext)"
              >
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Form Actions -->
          <div class="d-flex mt-2">
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="primary"
              class="mr-2"
              type="submit"
            >
              Add
            </b-button>
            <b-button
              v-ripple.400="'rgba(186, 191, 199, 0.15)'"
              type="button"
              variant="outline-secondary"
              @click="hide"
            >
              Cancel
            </b-button>
          </div>
        </b-form>
      </validation-observer>
    </template>
  </b-sidebar>
</template>

<script>
import {
  BSidebar,
  BForm,
  BFormGroup,
  BFormInput,
  BFormInvalidFeedback,
  BButton,
  BFormSpinbutton,
  BFormTextarea,
} from "bootstrap-vue";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import { ref, watch, computed } from "@vue/composition-api";
import { required, alphaNum, email } from "@validations";
import formValidation from "@core/comp-functions/forms/form-validation";
import Ripple from "vue-ripple-directive";
import vSelect from "vue-select";
import store from "@/store";

import useFaqsList from "./useFaqsList";

export default {
  components: {
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalidFeedback,
    BButton,
    vSelect,
    BFormSpinbutton,
    BFormTextarea,
    // Form Validation
    ValidationProvider,
    ValidationObserver,
  },
  directives: {
    Ripple,
  },
  model: {
    prop: "isAddNewSidebarActive",
    event: "update:is-add-new-sidebar-active",
  },
  props: {
    isAddNewSidebarActive: {
      type: Boolean,
      required: true,
    },

    faqStatuses: {
      type: Array,
      required: true,
    },
    faqLanguage: {
      type: Array,
      required: true,
    },
    faqBelong: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      required,
      alphaNum,
      email,
    };
  },
  setup(props, { emit }) {
    const { addFaq } = useFaqsList();

    const blankFaqData = {
      name: "",
      slug: "",
      type: "",
      belong: "",
      language: "",
      isActive: true,
      displayOrder: 0,
    };
    const catData = ref(JSON.parse(JSON.stringify(blankFaqData)));
    const resetCatData = () => {
      catData.value = JSON.parse(JSON.stringify(blankFaqData));
    };

    const onSubmit = async () => {
      const response = await addFaq(catData.value);
      if (typeof response !== "undefined") {
        emit("refetch-data");
        emit("update:is-add-new-sidebar-active", false);
      }
    };

    const { refFormObserver, getValidationState, resetForm } =
      formValidation(resetCatData);

    return {
      catData,
      onSubmit,

      refFormObserver,
      getValidationState,
      resetForm,
    };
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";

#add-new-sidebar {
  .vs__dropdown-menu {
    max-height: 200px !important;
  }
}
</style>
<style scoped>
.b-sidebar.sidebar-lg {
  width: 34rem;
}
</style>
